import "@/styles/fareSearchInteraction.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { FLIGHT_DEALS_ONLY } from "@/lib/release";
import {
  ArrowRight, ChevronDown, Briefcase, CalendarDays, CheckCircle2, Clock3, Compass,
  Info, Loader2, Plane, Search, ShieldCheck, SlidersHorizontal,
} from "lucide-react";
import { toast } from "sonner";
import { Btn } from "@/components/Primitives";
import { EmptyState, ErrorState } from "@/components/States";
import { api, formatApiError, formatApiErrorDetail } from "@/lib/api";
import airports from "@/data/airports.json";

function nextMonthValue() {
  const date = new Date();
  date.setDate(1);
  date.setMonth(date.getMonth() + 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function monthLimit(offset) {
  const today = new Date();
  const date = new Date(today.getFullYear(), today.getMonth() + offset, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;
}

function money(value, currency = "INR") {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return "Price unavailable";
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency,
    maximumFractionDigits: 0,
  }).format(amount);
}

function fareDeltaLabel(value, baseline, currency = "INR") {
  const amount = Number(value);
  const base = Number(baseline);
  if (!Number.isFinite(amount) || !Number.isFinite(base)) return "";
  const delta = amount - base;
  if (delta <= 0) return "Same fare";
  return `+${money(delta, currency)}`;
}

function readableDate(value) {
  if (!value) return "";
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" });
}

function compactDate(value) {
  if (!value) return "";
  const parsed = new Date(`${value}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

function dateDiffDays(start, end) {
  if (!start || !end) return null;
  const startDate = new Date(`${start}T00:00:00`);
  const endDate = new Date(`${end}T00:00:00`);
  if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) return null;
  const diff = Math.round((endDate - startDate) / 86400000);
  return diff > 0 ? diff : null;
}

function nextDateValue(value) {
  if (!value) return "";
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return "";
  date.setDate(date.getDate() + 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}

function monthFromDate(value) {
  return value ? String(value).slice(0, 7) : "";
}

function cleanParams(params) {
  return Object.fromEntries(Object.entries(params).filter(([, value]) => value !== "" && value !== null && value !== undefined));
}

function resultKey(item) {
  return `${item.departureDate}-${item.returnDate}-${item.tripDays}-${item.price}-${item.destinationAirport || item.destinationName || ""}`;
}

function isAnywhereEngine(engine) {
  return engine === "google_travel_explore" || engine === "google_flights_deals";
}

function isNonstopDeal(item) {
  const stops = item?.stops;
  if (stops === 0) return true;
  const text = String(stops ?? "").trim().toLowerCase();
  return text === "0" || text === "nonstop" || text === "non-stop" || text === "direct";
}

const AIRPORT_OPTIONS = airports;
const AIRPORT_BY_CODE = new Map(AIRPORT_OPTIONS.map((airport) => [airport.code, airport]));
const POPULAR_AIRPORT_CODES = [
  "BOM", "DEL", "BKK", "BLR", "HYD", "MAA", "CCU", "GOI",
  "DXB", "SIN", "KUL", "HKT", "DPS", "LHR", "JFK", "SFO", "CDG", "AMS",
];
const POPULAR_AIRPORT_OPTIONS = POPULAR_AIRPORT_CODES
  .map((code) => AIRPORT_BY_CODE.get(code))
  .filter(Boolean);
const AIRPORT_SEARCH_LIMIT = 50;

const ANYWHERE_OPTION = {
  code: "ANYWHERE",
  city: "Anywhere",
  country: "",
  name: "Find cheap destinations from your origin",
};

const TRAVEL_CLASS_LABELS = {
  economy: "Economy",
  premium_economy: "Premium economy",
  business: "Business",
  first_class: "First",
};

const LAYOVER_OPTIONS = [
  ["", "Any"],
  ["120", "Up to 2h"],
  ["240", "Up to 4h"],
  ["360", "Up to 6h"],
  ["480", "Up to 8h"],
  ["720", "Up to 12h"],
  ["1080", "Up to 18h"],
  ["1440", "Up to 24h"],
];

const LOADING_STEPS = [
  {
    title: "Search request sent",
    description: "Route, cabin, month, and layover choices are on their way.",
    threshold: 0,
  },
  {
    title: "Fare provider checking live options",
    description: "Waiting on the flight data provider to return available fare options.",
    threshold: 5,
  },
  {
    title: "Filtering dates and layovers",
    description: "Narrowing the response to the trip length and comfort filters you selected.",
    threshold: 12,
  },
  {
    title: "Preparing results",
    description: "This stays in progress until the search response is ready to display.",
    threshold: 20,
  },
];

const OTHER_RESULTS_BATCH_SIZE = 4;

function estimateSearchSeconds(mode, isAnywhereDestination) {
  if (isAnywhereDestination) return 18;
  if (mode === "date") return 16;
  return 28;
}

function formatWaitTime(seconds) {
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  const remainder = seconds % 60;
  return remainder ? `${minutes}m ${remainder}s` : `${minutes}m`;
}

function travelClassLabel(value) {
  return TRAVEL_CLASS_LABELS[value] || "Economy";
}

function layoverLabel(value) {
  if (!value) return "Any layover";
  const minutes = Number(value);
  if (!Number.isFinite(minutes)) return "Any layover";
  const hours = Math.round(minutes / 60);
  return `Layover up to ${hours}h`;
}

const compactInputCls =
  "w-full min-h-9 rounded-lg border border-[#D8CEC0] bg-white px-2.5 py-1.5 text-[13px] font-semibold text-[#17211D] shadow-[0_7px_16px_rgba(23,33,29,0.05)] placeholder:text-[#9A9286] focus:border-[#C5983C] focus:outline-none focus:ring-2 focus:ring-[#E5B85C]/35";

function InfoTip({ text, align = "left" }) {
  return (
    <span className="group relative inline-flex">
      <Info
        className="h-3 w-3 cursor-help text-[#2D7A65]"
        aria-label={text}
        role="img"
        tabIndex={0}
      />
      <span
        className={`pointer-events-none absolute top-5 z-30 hidden w-56 rounded-lg border border-[#17211D]/10 bg-[#FFFCF7] p-2 text-[11px] font-semibold leading-4 tracking-normal text-[#516259] shadow-xl group-hover:block group-focus-within:block ${align === "right" ? "right-0" : "left-0"}`}
      >
        {text}
      </span>
    </span>
  );
}

function PremiumField({ label, info, required, children, className = "" }) {
  return (
    <label className={`block min-w-0 ${className}`}>
      <span className="mb-1 flex items-center gap-1 text-[11px] font-extrabold text-[#34443F]">
        {label}
        {required && <span className="text-rose-500">*</span>}
        {info && <InfoTip text={info} />}
      </span>
      {children}
    </label>
  );
}

function airportDetails(value, includeAnywhere = false) {
  const code = String(value || "").trim().toUpperCase();
  if (includeAnywhere && code === "ANYWHERE") return ANYWHERE_OPTION;
  return AIRPORT_BY_CODE.get(code) || {
    code: code || "",
    city: code || "Select airport",
    country: "",
    name: code ? "Select an airport from the list" : "Choose from popular searches",
  };
}

function airportLocation(option) {
  return [option?.city, option?.country].filter(Boolean).join(", ") || option?.city || "";
}

function RoutePicker({ label, value, includeAnywhere = false, active, query, align = "left", onOpen, onClose, onQuery, onSelect }) {
  const pickerRef = useRef(null);
  const selected = airportDetails(value, includeAnywhere);
  const normalizedQuery = query.trim().toLowerCase();
  const defaultOptions = includeAnywhere ? [ANYWHERE_OPTION, ...POPULAR_AIRPORT_OPTIONS] : POPULAR_AIRPORT_OPTIONS;
  const searchOptions = includeAnywhere ? [ANYWHERE_OPTION, ...AIRPORT_OPTIONS] : AIRPORT_OPTIONS;
  const options = normalizedQuery ? searchOptions : defaultOptions;
  const filtered = options.filter((option) => {
    const haystack = `${option.code} ${option.city} ${option.country || ""} ${option.name}`.toLowerCase();
    return !normalizedQuery || haystack.includes(normalizedQuery);
  });
  const visibleOptions = normalizedQuery ? filtered.slice(0, AIRPORT_SEARCH_LIMIT) : filtered;

  useEffect(() => {
    if (!active) return undefined;

    const handlePointerDown = (event) => {
      if (!pickerRef.current?.contains(event.target)) {
        onClose();
      }
    };

    document.addEventListener("pointerdown", handlePointerDown, true);
    return () => document.removeEventListener("pointerdown", handlePointerDown, true);
  }, [active, onClose]);

  return (
    <div ref={pickerRef} className="fare-airport-picker relative min-w-0">
      <button
        type="button"
        onClick={active ? onClose : onOpen}
        aria-expanded={active}
        aria-label={`${label}: ${airportLocation(selected)} (${selected.code}). Change airport`}
        className={`w-full rounded-xl px-3 py-2 text-left transition hover:bg-white/10 focus:outline-none focus-visible:outline-none ${align === "right" ? "text-right" : ""}`}
      >
        <span className="fare-airport-label">{label}<ChevronDown size={14} aria-hidden="true" /></span>
        <span className="mt-0.5 block truncate text-xl font-black leading-none text-[#FFFAF1]">{selected.code || value || "Select"}</span>
        <span className="mt-1 block truncate text-[11px] font-semibold text-[#FFFAF1]/68">{airportLocation(selected)}</span>
      </button>

      {active && (
        <div className={`absolute top-[calc(100%+0.4rem)] z-40 w-[min(21rem,80vw)] overflow-hidden rounded-xl border border-[#D8CEC0] bg-white text-[#17211D] shadow-2xl ${align === "right" ? "right-0" : "left-0"}`}>
          <div className="border-b border-[#17211D]/10 px-3 py-2">
            <div className="flex items-center gap-2 rounded-lg bg-[#F7F1E7] px-2.5 py-2">
              <Search className="h-3.5 w-3.5 text-[#516259]" />
              <input
                autoFocus
                className="w-full border-0 bg-transparent p-0 text-sm font-semibold text-[#17211D] outline-none placeholder:text-[#8A8176] focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
                value={query}
                onChange={(event) => onQuery(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    if (visibleOptions.length > 0) {
                      onSelect(visibleOptions[0]);
                    }
                  }
                }}
                placeholder={`Search ${label.toLowerCase()}`}
              />
            </div>
          </div>
          <div className="max-h-72 overflow-y-auto p-2">
            <p className="px-2 pb-1 text-[11px] font-black text-[#516259]">
              {normalizedQuery ? `Matching airports (${filtered.length})` : "Popular searches"}
            </p>
            {visibleOptions.map((option) => (
              <button
                key={option.code}
                type="button"
                onClick={() => onSelect(option)}
                className="flex w-full items-center gap-3 rounded-lg px-2 py-2 text-left outline-none hover:bg-[#F7F1E7] focus:bg-[#F7F1E7] focus-visible:outline-none"
              >
                <span className="grid h-10 w-12 shrink-0 place-items-center rounded-lg border border-[#17211D]/10 bg-[#F7F1E7] text-xs font-black text-[#17211D]">
                  {option.code === "ANYWHERE" ? "ANY" : option.code}
                </span>
                <span className="min-w-0">
                  <span className="block truncate text-sm font-black text-[#17211D]">{airportLocation(option)}</span>
                  <span className="block truncate text-xs font-semibold text-[#516259]">{option.name}</span>
                </span>
              </button>
            ))}
            {filtered.length === 0 && normalizedQuery && (
              <p className="rounded-lg px-2 py-3 text-sm font-semibold text-[#516259]">
                No matching airport found.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DealCard({ item, currency, highlightCheapest = false, baselinePrice = null }) {
  const deltaBadge = fareDeltaLabel(item.price, baselinePrice, currency) || item.rankLabel;
  return (
    <article className="tp-paper flex h-full flex-col rounded-xl p-2.5 sm:rounded-[1.1rem] sm:p-3">
      <div className="min-w-0">
        <div className="flex min-h-6 flex-wrap gap-1">
          <p className="inline-flex max-w-full items-center gap-1 rounded-full bg-[#E7F2ED] px-2 py-0.5 text-[11px] font-black leading-4 text-[#2D7A65]">
            {highlightCheapest && <CheckCircle2 className="h-3 w-3 shrink-0" />}
            <span className="whitespace-nowrap">
              {highlightCheapest ? "Cheapest" : deltaBadge}
            </span>
          </p>
          {item.isRequestedDates && (
            <p className="inline-flex max-w-full items-center gap-1 rounded-full bg-[#FFF3D2] px-2 py-0.5 text-[10px] font-black leading-4 text-[#7A5D2D]">
              Exact
            </p>
          )}
        </div>
        <h3 className="mt-2 truncate text-base font-black text-[#17211D] sm:text-lg">{money(item.price, currency)}</h3>
        {item.destinationName && (
          <p className="mt-0.5 line-clamp-2 text-[11px] font-black leading-4 text-[#254F43] sm:text-xs">
            {item.destinationName}{item.destinationCountry ? `, ${item.destinationCountry}` : ""}
          </p>
        )}
      </div>
      <div className="mt-2 grid gap-1 text-[11px] font-semibold leading-4 text-[#516259] sm:mt-3 sm:text-xs">
        <p className="flex min-w-0 items-center gap-1.5" title={`Depart ${readableDate(item.departureDate)}`}>
          <Plane className="h-3.5 w-3.5 shrink-0 text-[#2D7A65]" />
          <span className="truncate">Dep {compactDate(item.departureDate)}</span>
        </p>
        <p className="flex min-w-0 items-center gap-1.5" title={`Return ${readableDate(item.returnDate)}`}>
          <CalendarDays className="h-3.5 w-3.5 shrink-0 text-[#2D7A65]" />
          <span className="truncate">Ret {compactDate(item.returnDate)}</span>
        </p>
        <p className="flex min-w-0 items-center gap-1.5">
          <Clock3 className="h-3.5 w-3.5 shrink-0 text-[#2D7A65]" />
          <span className="truncate">{item.durationLabel || `${item.tripDays} days`}</span>
        </p>
      </div>
      <a
        href={item.liveFareUrl}
        target="_blank"
        rel="noreferrer"
        className="mt-2 inline-flex min-h-8 w-full items-center justify-between gap-2 rounded-md bg-[#E5B85C] px-2.5 py-1.5 text-[11px] font-black text-[#17211D] hover:bg-[#f0c96f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65] focus-visible:ring-offset-2 sm:min-h-9 sm:text-xs"
      >
        <span className="whitespace-nowrap">Book now</span>
        <ArrowRight className="h-3.5 w-3.5 shrink-0" />
      </a>
    </article>
  );
}

function DealRail({ children }) {
  return (
    <div className="-mx-1 overflow-x-auto px-1 pr-7 [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
      <div className="flex snap-x gap-2 sm:gap-3">
        {children}
      </div>
    </div>
  );
}

export default function FlightFareCalendar({ embedded = false }) {
  const [form, setForm] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    const preferred = params.get("origin") || "BOM";
    return {
    origin: /^[a-z]{3}$/i.test(preferred) ? preferred.toUpperCase() : "BOM",
    destination: "BKK",
    trip_days: 6,
    trip_duration: "one_week",
    month: nextMonthValue(),
    outbound_date: "",
    return_date: "",
    travel_class: "economy",
    max_layover_minutes: "",
  }; });
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchMode, setSearchMode] = useState("month");
  const [activeRoutePicker, setActiveRoutePicker] = useState(null);
  const [routeQuery, setRouteQuery] = useState("");
  const [restFilter, setRestFilter] = useState("all");
  const [loadingElapsedSeconds, setLoadingElapsedSeconds] = useState(0);
  const [visibleOtherCount, setVisibleOtherCount] = useState(OTHER_RESULTS_BATCH_SIZE);
  const otherResultsLoadRef = useRef(null);
  const isAnywhereDestination = form.destination.trim().toLowerCase() === "anywhere";
  const focusSearchField = (field) => {
    closeRoutePicker();
    const input = document.querySelector(`#smart-fare-search [data-search-field="${field}"]`);
    if (!input) return;
    input.scrollIntoView?.({ block: "center", behavior: window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ? "auto" : "smooth" });
    input.focus({ preventScroll: true });
    if (["date", "month"].includes(input.type)) {
      try { input.showPicker?.(); } catch { /* Focus remains available if native picker is unsupported. */ }
    }
  };
  const exactTripDays = dateDiffDays(form.outbound_date, form.return_date) || 0;

  const results = useMemo(() => result?.results || [], [result]);
  const requestedTripDays = Number(form.trip_days);
  const anywhereDurationLabel = { weekend: "Weekend", one_week: "One week", two_weeks: "Two weeks" }[form.trip_duration];
  const requestedDurationResults = useMemo(() => (
    result?.requestedDurationResults?.length
      ? result.requestedDurationResults
      : results.filter((item) => item.tripDays === requestedTripDays).slice(0, 3)
  ), [result, requestedTripDays, results]);
  const nearbyDurationResults = useMemo(() => (
    isAnywhereEngine(result?.engine) ? [] : result?.nearbyDurationResults?.length
      ? result.nearbyDurationResults
      : results.filter((item) => item.tripDays !== requestedTripDays).slice(0, 3)
  ), [result, requestedTripDays, results]);
  const featuredKeys = useMemo(() => (
    new Set([...requestedDurationResults, ...nearbyDurationResults].map(resultKey))
  ), [requestedDurationResults, nearbyDurationResults]);
  const otherResults = useMemo(() => (
    results.filter((item) => !featuredKeys.has(resultKey(item)))
  ), [featuredKeys, results]);
  const isAnywhereResult = isAnywhereEngine(result?.engine);
  const otherBestPrice = otherResults.length
    ? Math.min(...otherResults.map((item) => Number(item.price)).filter(Number.isFinite))
    : null;
  const filteredOtherResults = useMemo(() => {
    if (isAnywhereResult) {
      if (restFilter === "lowest") {
        if (!Number.isFinite(otherBestPrice)) return otherResults;
        return otherResults.filter((item) => Number(item.price) <= otherBestPrice * 1.15);
      }
      if (restFilter === "nonstop") {
        return otherResults.filter(isNonstopDeal);
      }
      return otherResults;
    }
    if (restFilter === "requested") {
      return otherResults.filter((item) => item.tripDays === requestedTripDays);
    }
    if (restFilter === "shorter") {
      return otherResults.filter((item) => item.tripDays < requestedTripDays);
    }
    if (restFilter === "longer") {
      return otherResults.filter((item) => item.tripDays > requestedTripDays);
    }
    return otherResults;
  }, [isAnywhereResult, otherBestPrice, otherResults, requestedTripDays, restFilter]);
  const visibleOtherResults = useMemo(() => (
    filteredOtherResults.slice(0, visibleOtherCount)
  ), [filteredOtherResults, visibleOtherCount]);
  const hasMoreOtherResults = visibleOtherCount < filteredOtherResults.length;
  const cheapest = result?.bestRequestedDurationDeal || requestedDurationResults[0] || results[0];
  const exactDeal = result?.exactDeal;
  const requestedResultsToShow = exactDeal && searchMode === "date"
    ? requestedDurationResults.filter((item) => resultKey(item) !== resultKey(exactDeal))
    : requestedDurationResults;
  const requestedBestPrice = requestedDurationResults.length
    ? Math.min(...requestedDurationResults.map((item) => Number(item.price)).filter(Number.isFinite))
    : null;
  const nearbyBestPrice = nearbyDurationResults.length
    ? Math.min(...nearbyDurationResults.map((item) => Number(item.price)).filter(Number.isFinite))
    : null;
  const filteredOtherBestPrice = filteredOtherResults.length
    ? Math.min(...filteredOtherResults.map((item) => Number(item.price)).filter(Number.isFinite))
    : null;
  const resultSummary = useMemo(() => {
    if (!result) return "";
    const total = result.totalDeals || results.length;
    if (result.engine === "google_travel_explore" || result.engine === "google_flights_deals") {
      return `${total} handpicked options found for your trip style.`;
    }
    return `${total} promising fare options are ready for your trip.`;
  }, [result, results.length]);
  const estimatedSearchSeconds = useMemo(
    () => estimateSearchSeconds(searchMode, isAnywhereDestination),
    [isAnywhereDestination, searchMode],
  );
  const activeLoadingStep = useMemo(() => {
    let index = 0;
    LOADING_STEPS.forEach((step, stepIndex) => {
      if (loadingElapsedSeconds >= step.threshold) {
        index = stepIndex;
      }
    });
    return Math.min(index, LOADING_STEPS.length - 1);
  }, [loadingElapsedSeconds]);
  const remainingWaitSeconds = Math.max(0, estimatedSearchSeconds - loadingElapsedSeconds);

  useEffect(() => {
    if (!loading) {
      setLoadingElapsedSeconds(0);
      return undefined;
    }
    const startedAt = Date.now();
    setLoadingElapsedSeconds(0);
    const interval = setInterval(() => {
      setLoadingElapsedSeconds(Math.floor((Date.now() - startedAt) / 1000));
    }, 1000);
    return () => clearInterval(interval);
  }, [loading]);

  useEffect(() => {
    setVisibleOtherCount(OTHER_RESULTS_BATCH_SIZE);
  }, [restFilter, result]);

  useEffect(() => {
    setRestFilter("all");
  }, [isAnywhereResult]);

  useEffect(() => {
    if (!hasMoreOtherResults) return undefined;
    const node = otherResultsLoadRef.current;
    if (!node || typeof IntersectionObserver === "undefined") return undefined;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisibleOtherCount((count) => Math.min(count + OTHER_RESULTS_BATCH_SIZE, filteredOtherResults.length));
        }
      },
      { root: null, rootMargin: "220px 0px", threshold: 0.01 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [filteredOtherResults.length, hasMoreOtherResults, visibleOtherCount]);

  const setField = (key, value) => setForm((prev) => ({ ...prev, [key]: value }));

  const openRoutePicker = (picker) => {
    setActiveRoutePicker(picker);
    setRouteQuery("");
  };

  const closeRoutePicker = () => {
    setActiveRoutePicker(null);
    setRouteQuery("");
  };

  const chooseOrigin = (option) => {
    setField("origin", option.code);
    closeRoutePicker();
  };

  const chooseDestination = (option) => {
    updateDestination(option.code);
    closeRoutePicker();
  };

  const updateDestination = (value) => {
    setResult(null);
    setError("");
    if (value === "ANYWHERE") setSearchMode("month");
    setForm((prev) => ({ ...prev, destination: value,
      ...(value === "ANYWHERE" ? { outbound_date: "", return_date: "" } : {}),
    }));
  };

  const setMode = (mode) => {
    setResult(null);
    setError("");
    setSearchMode(mode === "anywhere" ? "month" : mode);
    setForm((prev) => ({
      ...prev,
      destination: mode === "anywhere" ? "ANYWHERE" : prev.destination === "ANYWHERE" ? "BKK" : prev.destination,
      outbound_date: mode === "date" ? prev.outbound_date : "",
      return_date: "",
    }));
  };

  const updateOutboundDate = (value) => {
    setForm((prev) => ({
      ...prev,
      outbound_date: value,
      return_date: prev.return_date && dateDiffDays(value, prev.return_date) ? prev.return_date : "",
      trip_days: dateDiffDays(value, prev.return_date) || prev.trip_days,
    }));
  };

  const updateReturnDate = (value) => {
    setForm((prev) => ({
      ...prev,
      return_date: value,
      trip_days: dateDiffDays(prev.outbound_date, value) || prev.trip_days,
    }));
  };

  const searchFares = async (event) => {
    event.preventDefault();
    if (searchMode === "date" && !dateDiffDays(form.outbound_date, form.return_date)) {
      toast.error("Return date must be after the departure date.");
      return;
    }
    setLoading(true);
    setLoadingElapsedSeconds(0);
    setError("");
    setResult(null);
    try {
      const params = cleanParams({
        origin: form.origin.trim(),
        destination: isAnywhereDestination ? "" : form.destination.trim(),
        search_mode: isAnywhereDestination ? "anywhere" : searchMode === "date" ? "exact" : "route",
        currency: "INR",
        month: searchMode === "date" ? monthFromDate(form.outbound_date) : form.month,
        trip_days: isAnywhereDestination ? "" : searchMode === "date" ? exactTripDays : Number(form.trip_days),
        trip_duration: isAnywhereDestination ? form.trip_duration : "",
        outbound_date: searchMode === "date" ? form.outbound_date : "",
        return_date: searchMode === "date" ? form.return_date : "",
        travel_class: form.travel_class,
        max_layover_minutes: isAnywhereDestination ? "" : form.max_layover_minutes,
        limit: 200,
      });
      const { data } = await api.get("/flight-fares/calendar", { params });
      setResult(data);
    } catch (err) {
      const message = formatApiError(err, "Could not search flight fares.");
      setError(message);
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || message);
    } finally {
      setLoading(false);
    }
  };

  const shellClass = embedded
    ? "font-[Inter,sans-serif] text-[#17211D]"
    : "tp-app-shell min-h-screen p-2.5 font-[Inter,sans-serif] text-[#17211D] sm:p-6";
  const sectionTitleClass = embedded ? "text-[#516259]" : "text-[#FFFAF1]";
  const Shell = embedded ? "div" : "main";

  return (
    <Shell className={shellClass} data-testid="flight-fare-calendar">
      <div className="mx-auto max-w-7xl">
        {embedded ? (
          <section className="fare-page-heading mb-4 flex flex-col gap-3 rounded-2xl border border-[#E2DFD7] bg-white/65 p-4 shadow-[0_10px_28px_rgba(37,49,43,0.05)] sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-[10px] font-black uppercase tracking-[0.18em] text-[#E5B85C]">A little flexibility. A world of possibilities.</p>
              <h1 className="mt-1 font-[Plus_Jakarta_Sans,sans-serif] text-2xl font-extrabold leading-tight text-[#17211D] sm:text-3xl">Where will your next flight take you?</h1>
              <p className="mt-1 text-sm font-semibold leading-6 text-[#516259]">Explore better dates, compare fares, and find a journey that fits.</p>
            </div>
            {!FLIGHT_DEALS_ONLY && <Link to="/discover" className="inline-flex min-h-10 items-center justify-center gap-2 rounded-xl border border-[#E2DFD7] bg-white px-3 text-sm font-black text-[#254F43] shadow-sm hover:bg-[#F7F1E5]">
              Public trips <ArrowRight className="h-4 w-4" />
            </Link>}
          </section>
        ) : (
          <section className="relative overflow-hidden rounded-[1.25rem] border border-white/20 bg-[#10231C] p-4 text-[#FFFAF1] shadow-2xl sm:rounded-[2rem] sm:p-8">
            <img src="https://images.unsplash.com/photo-1436491865332-7a61a109cc05?auto=format&fit=crop&w=1800&q=80" alt="" className="absolute inset-0 h-full w-full object-cover opacity-50" />
            <div className="absolute inset-0 bg-gradient-to-r from-[#07120F]/95 via-[#10231C]/78 to-[#10231C]/25" />
            <div className="relative">
              <div className="mb-5 flex items-center justify-between gap-3 sm:mb-8">
                <Link to={FLIGHT_DEALS_ONLY ? "/flight-deals" : "/discover"} className="inline-flex items-center gap-2 text-sm font-bold text-[#FFFAF1]">
                  <Compass className="h-5 w-5 text-[#E5B85C]" /> TrailPulse
                </Link>
                {!FLIGHT_DEALS_ONLY && <Link to="/discover" className="inline-flex min-h-9 items-center gap-1.5 rounded-md border border-white/25 px-3 text-xs font-bold text-white hover:bg-white/10 sm:min-h-10 sm:gap-2 sm:px-4 sm:text-sm">
                  Public trips <ArrowRight className="h-4 w-4" />
                </Link>}
              </div>
              <p className="mb-2 text-[11px] font-extrabold uppercase tracking-[0.18em] text-[#E5B85C] sm:mb-3 sm:text-xs sm:tracking-[0.24em]">Flight fare finder</p>
              <h1 className="max-w-4xl font-[Plus_Jakarta_Sans,sans-serif] text-2xl font-extrabold leading-[1.05] sm:text-6xl">
                Find the cheapest dates before you plan.
              </h1>
            </div>
          </section>
        )}

        <section className={`fare-search-grid ${embedded ? "mt-0" : "mt-3 sm:mt-5"} grid gap-3 sm:gap-5 xl:grid-cols-[minmax(0,28rem)_1fr]`}>
          <form id="smart-fare-search" onSubmit={searchFares} className="fare-search-form min-h-[18rem] overflow-visible rounded-[1.1rem] border border-[#D7C7A7] bg-[#FFFDF8] shadow-[0_22px_58px_rgba(23,33,29,0.18)] sm:min-h-[34rem]">
            <div className="rounded-t-[1.1rem] bg-[#10231C] px-3 py-2.5 text-[#FFFAF1]">
              <div className="flex items-center justify-between gap-3">
                <p className="flex items-center gap-2 text-[13px] font-black">
                  <SlidersHorizontal className="h-3.5 w-3.5 text-[#E5B85C]" /> Smart fare search
                </p>
                <InfoTip
                  align="right"
                  text="Pick a fixed route, exact route dates, or anywhere deals from your origin."
                />
              </div>
              <div className="mt-2 grid grid-cols-[1fr_auto_1fr] items-center gap-2 rounded-xl border border-white/10 bg-white/10 px-3 py-1.5">
                <RoutePicker
                  label="From"
                  value={form.origin}
                  active={activeRoutePicker === "origin"}
                  query={activeRoutePicker === "origin" ? routeQuery : ""}
                  onOpen={() => openRoutePicker("origin")}
                  onClose={closeRoutePicker}
                  onQuery={setRouteQuery}
                  onSelect={chooseOrigin}
                />
                <div className="grid h-7 w-7 place-items-center rounded-full bg-[#E5B85C] text-[#17211D]">
                  <Plane className="h-3.5 w-3.5" />
                </div>
                <RoutePicker
                  label="To"
                  value={form.destination}
                  includeAnywhere
                  active={activeRoutePicker === "destination"}
                  query={activeRoutePicker === "destination" ? routeQuery : ""}
                  align="right"
                  onOpen={() => openRoutePicker("destination")}
                  onClose={closeRoutePicker}
                  onQuery={setRouteQuery}
                  onSelect={chooseDestination}
                />
              </div>
            </div>

            <div className="grid gap-2.5 p-2.5 sm:p-3">
              <div className="fare-mode-switch rounded-xl border border-[#E7DDCF] bg-[#F7F1E7] p-1">
                <div className="grid grid-cols-3 gap-1">
                  <button
                    type="button"
                    aria-pressed={searchMode === "month" && !isAnywhereDestination}
                    onClick={() => setMode("month")}
                    className={`min-h-9 rounded-lg px-2 text-[13px] font-black transition ${searchMode === "month" && !isAnywhereDestination ? "bg-white text-[#17211D] shadow-sm" : "text-[#5A625D] hover:bg-white/55"}`}
                  >
                    Flexible month
                  </button>
                  <button
                    type="button"
                    aria-pressed={searchMode === "date" && !isAnywhereDestination}
                    onClick={() => setMode("date")}
                    className={`min-h-9 rounded-lg px-2 text-[13px] font-black transition ${searchMode === "date" && !isAnywhereDestination ? "bg-white text-[#17211D] shadow-sm" : "text-[#5A625D] hover:bg-white/55"}`}
                  >
                    Exact dates
                  </button>
                  <button type="button" aria-pressed={isAnywhereDestination} onClick={() => setMode("anywhere")} className={`min-h-9 rounded-lg px-2 text-[13px] font-black transition ${isAnywhereDestination ? "bg-white text-[#17211D] shadow-sm" : "text-[#5A625D] hover:bg-white/55"}`}>Anywhere</button>
                </div>
              </div>

              {searchMode === "date" ? (
                <>
                  <div className="grid grid-cols-2 gap-2">
                    <PremiumField label="Departure" required info="Start date for the exact fare check.">
                      <input
                        type="date"
                        data-search-field="departure"
                        className={compactInputCls}
                        value={form.outbound_date}
                        onChange={(e) => updateOutboundDate(e.target.value)}
                        required
                      />
                    </PremiumField>
                    <PremiumField label="Return" required info="End date for the exact fare check. Trip length updates automatically.">
                      <input
                        type="date"
                        data-search-field="return"
                        min={nextDateValue(form.outbound_date)}
                        className={compactInputCls}
                        value={form.return_date}
                        onChange={(e) => updateReturnDate(e.target.value)}
                        required
                      />
                    </PremiumField>
                  </div>
                  <div className={`grid gap-2 ${isAnywhereDestination ? "grid-cols-1" : "grid-cols-2"}`}>
                    <PremiumField label="Cabin class" info="Fare search and prices use this cabin class.">
                      <select data-search-field="cabin" className={compactInputCls} value={form.travel_class} onChange={(e) => setField("travel_class", e.target.value)}>
                        <option value="economy">Economy</option>
                        <option value="premium_economy">Premium economy</option>
                        <option value="business">Business</option>
                        <option value="first_class">First</option>
                      </select>
                    </PremiumField>
                    {!isAnywhereDestination && (
                      <PremiumField label="Max layover" info="Caps each stopover duration for 1-stop flights. Any allows all layover lengths.">
                        <select className={compactInputCls} value={form.max_layover_minutes} onChange={(e) => setField("max_layover_minutes", e.target.value)}>
                          {LAYOVER_OPTIONS.map(([value, label]) => (
                            <option key={value || "any"} value={value}>{label}</option>
                          ))}
                        </select>
                      </PremiumField>
                    )}
                  </div>
                  <div className="flex items-center justify-between gap-2 rounded-lg border border-[#E7DDCF] bg-[#F7F1E7] px-3 py-2">
                    <p className="flex min-w-0 items-center gap-2 text-[12px] font-black text-[#516259]">
                      <Clock3 className="h-3.5 w-3.5 shrink-0 text-[#2D7A65]" />
                      <span className="truncate">Trip length</span>
                    </p>
                    <span className="shrink-0 rounded-full bg-white px-2.5 py-1 text-[12px] font-black text-[#17211D] shadow-sm">
                      {exactTripDays} days
                    </span>
                  </div>
                </>
              ) : (
                <div className="grid gap-2">
                  <div className="grid grid-cols-[0.85fr_1.15fr] gap-2">
                    <PremiumField
                      label="Trip length"
                      required
                      info={isAnywhereDestination ? "Choose Weekend, One week or Two weeks. Actual dates and trip lengths vary by fare." : "Month scans compare this trip length plus two shorter and two longer durations."}
                    >
                      {isAnywhereDestination ? (
                        <select data-search-field="duration" className={compactInputCls} value={form.trip_duration} onChange={(e) => setField("trip_duration", e.target.value)} required>
                          <option value="weekend">Weekend</option>
                          <option value="one_week">One week</option>
                          <option value="two_weeks">Two weeks</option>
                        </select>
                      ) : <input
                        type="number"
                        data-search-field="duration"
                        min="1"
                        max="45"
                        className={compactInputCls}
                        value={form.trip_days}
                        onChange={(e) => setField("trip_days", e.target.value)}
                        required
                      />}
                    </PremiumField>
                    <PremiumField
                      label="Travel month"
                      required
                      info={isAnywhereDestination ? "Finds destinations for your selected month and flexible trip length." : "Scans every departure day in this month for the selected trip length and nearby durations."}
                    >
                      <input type="month" data-search-field="month" min={isAnywhereDestination ? monthLimit(0) : undefined} max={isAnywhereDestination ? monthLimit(6) : undefined} className={`${compactInputCls} px-2 text-[12px]`} value={form.month} onChange={(e) => setField("month", e.target.value)} required />
                    </PremiumField>
                  </div>
                  <div className={`grid gap-2 ${isAnywhereDestination ? "grid-cols-1" : "grid-cols-2"}`}>
                    <PremiumField label="Cabin class" info="Fare search and prices use this cabin class.">
                      <select data-search-field="cabin" className={compactInputCls} value={form.travel_class} onChange={(e) => setField("travel_class", e.target.value)}>
                        <option value="economy">Economy</option>
                        <option value="premium_economy">Premium economy</option>
                        <option value="business">Business</option>
                        <option value="first_class">First</option>
                      </select>
                    </PremiumField>
                    {!isAnywhereDestination && (
                      <PremiumField label="Max layover" info="Caps each stopover duration for 1-stop flights. Any allows all layover lengths.">
                        <select className={compactInputCls} value={form.max_layover_minutes} onChange={(e) => setField("max_layover_minutes", e.target.value)}>
                          {LAYOVER_OPTIONS.map(([value, label]) => (
                            <option key={value || "any"} value={value}>{label}</option>
                          ))}
                        </select>
                      </PremiumField>
                    )}
                  </div>
                </div>
              )}

              <Btn type="submit" disabled={loading} className="min-h-10 w-full rounded-lg text-sm font-black shadow-[0_14px_26px_rgba(197,152,60,0.22)]">
                {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                {loading ? "Finding your best deals..." : "Find best fares"}
              </Btn>

              <div className="flex items-center justify-between gap-2 rounded-lg bg-[#F7F1E7] px-3 py-1.5 text-[11px] font-bold text-[#516259]">
                <p className="flex items-center gap-2">
                  <ShieldCheck className="h-4 w-4 text-[#2D7A65]" /> Live fares can change
                </p>
                <InfoTip
                  align="right"
                  text="Displayed fares are subject to changes in airline pricing"
                />
              </div>
            </div>
          </form>

          <section className="min-w-0">
            {error ? (
              <ErrorState message={error} onRetry={() => searchFares({ preventDefault() {} })} />
            ) : loading ? (
              <div className="tp-paper min-h-[18rem] rounded-[1.15rem] p-4 sm:min-h-[34rem] sm:rounded-[1.5rem] sm:p-7">
                <div className="flex min-h-[16rem] flex-col justify-center">
                  <div className="mx-auto grid h-16 w-16 place-items-center rounded-full bg-[#E7F2ED]">
                    <div className="grid h-12 w-12 place-items-center rounded-full border-2 border-[#2D7A65]/25 border-t-[#2D7A65] animate-spin">
                      <Plane className="h-5 w-5 text-[#2D7A65]" />
                    </div>
                  </div>

                  <div className="mx-auto mt-4 max-w-xl text-center">
                    <p className="text-[11px] font-black uppercase tracking-[0.18em] text-[#2D7A65]">Deal hunt in progress</p>
                    <h2 className="mt-2 text-xl font-black leading-tight text-[#17211D] sm:text-3xl">Hold tight, your best dates are taking shape.</h2>
                    <p className="mt-2 text-sm font-semibold leading-6 text-[#516259]">
                      We are polishing the strongest options for your trip so the best choices rise to the top.
                    </p>
                  </div>

                  <div className="mx-auto mt-5 flex w-full max-w-xl flex-wrap items-center justify-between gap-2 text-xs font-bold text-[#516259]">
                    <span>Elapsed {formatWaitTime(loadingElapsedSeconds)}</span>
                    <span>
                      {remainingWaitSeconds > 0
                        ? `Usually about ${formatWaitTime(remainingWaitSeconds)} more`
                        : "Taking longer than usual, still checking"}
                    </span>
                  </div>

                  <div className="mx-auto mt-5 grid w-full max-w-xl gap-2">
                    {LOADING_STEPS.map(({ title, description }, index) => {
                      const done = index < activeLoadingStep;
                      const active = index === activeLoadingStep;
                      return (
                        <div key={title} className={`rounded-xl border p-3 text-left shadow-[0_10px_22px_rgba(23,33,29,0.05)] transition ${done ? "border-[#2D7A65]/20 bg-[#FFFCF7]" : active ? "border-[#E5B85C]/45 bg-[#FFFCF7]" : "border-[#17211D]/10 bg-[#FFFCF7]/70"}`}>
                          <p className="flex items-center gap-1.5 text-[12px] font-black text-[#17211D]">
                            {done ? (
                              <CheckCircle2 className="h-3.5 w-3.5 text-[#2D7A65]" />
                            ) : active ? (
                              <Loader2 className="h-3.5 w-3.5 animate-spin text-[#C5983C]" />
                            ) : (
                              <span className="h-3.5 w-3.5 rounded-full border-2 border-[#2D7A65]/25" />
                            )}
                            {title}
                        </p>
                          <p className="mt-1 text-[11px] font-semibold leading-4 text-[#516259]">{description}</p>
                        </div>
                      );
                    })}
                  </div>

                  <p className="mx-auto mt-5 max-w-md text-center text-xs font-bold leading-5 text-[#516259]">
                    Great deals deserve a careful look. Keep this open while we finish bringing the best ones together.
                  </p>
                </div>
              </div>
            ) : !result ? (
              <div className="fare-discovery-empty tp-paper grid min-h-[18rem] place-items-center rounded-[1.15rem] p-5 sm:min-h-[34rem] sm:rounded-[1.5rem] sm:p-8">
                <div className="fare-discovery-content">
                  <div className="fare-route-art" aria-hidden="true"><span className="fare-orbit fare-orbit-outer" /><span className="fare-orbit fare-orbit-inner" /><span className="fare-art-dot" /><Plane size={34} strokeWidth={1.3} /></div>
                  <p className="fare-empty-eyebrow">Make this trip yours</p>
                  <h2>{isAnywhereDestination ? "Where could you go?" : `${airportDetails(form.origin).city} to ${airportDetails(form.destination).city}`}</h2>
                  <p>{isAnywhereDestination ? "Start from your airport. Let the destinations surprise you." : "A different date could be your next great find. Set your plans, then explore the fares."}</p>
                  <div className="fare-live-trip" aria-label="Your search choices">
                    {searchMode === "date" ? <>
                      <button type="button" onClick={() => focusSearchField("departure")} aria-label="Change departure date"><CalendarDays size={15} aria-hidden="true" />{form.outbound_date ? compactDate(form.outbound_date) : "Departure date"}</button>
                      <button type="button" onClick={() => focusSearchField("return")} aria-label="Change return date"><CalendarDays size={15} aria-hidden="true" />{form.return_date ? compactDate(form.return_date) : "Return date"}</button>
                    </> : <>
                      <button type="button" onClick={() => focusSearchField("month")} aria-label="Change travel month"><CalendarDays size={15} aria-hidden="true" />{form.month ? new Date(`${form.month}-01T12:00:00`).toLocaleDateString("en-GB", { month: "long", year: "numeric" }) : "Choose a month"}</button>
                      <button type="button" onClick={() => focusSearchField("duration")} aria-label="Change trip length"><Clock3 size={15} aria-hidden="true" />{isAnywhereDestination ? anywhereDurationLabel : `${form.trip_days || "Your"} days`}</button>
                    </>}
                    <button type="button" onClick={() => focusSearchField("cabin")} aria-label="Change cabin class"><Briefcase size={15} aria-hidden="true" />{travelClassLabel(form.travel_class)}</button>
                  </div>
                  <div className="fare-start-actions">
                    <button type="button" onClick={() => openRoutePicker("origin")}>Change departure <ArrowRight size={14} /></button>
                    <button type="button" onClick={() => openRoutePicker("destination")}>{isAnywhereDestination ? "Pick a destination" : "Change destination"} <ArrowRight size={14} /></button>
                  </div>
                  <button type="submit" form="smart-fare-search" className="fare-start-search">Search this trip <Search size={16} aria-hidden="true" /></button>
                </div>
              </div>
            ) : results.length === 0 ? (
              <div className="tp-paper grid min-h-[18rem] place-items-center rounded-[1.15rem] p-5 sm:min-h-[34rem] sm:rounded-[1.5rem] sm:p-8">
                <EmptyState icon={CalendarDays} title="No fare combinations found" description="Try a nearby month, fewer stop restrictions, or another destination airport." />
              </div>
            ) : (
              <div className="space-y-3 sm:space-y-5">
                <div className="tp-dark-panel rounded-[1.15rem] p-4 text-[#FFFAF1] sm:rounded-[1.5rem] sm:p-5">
                  <div className="grid gap-3 sm:grid-cols-[1fr_auto] sm:items-start">
                    <div className="min-w-0">
                      <p className="text-[11px] font-black uppercase tracking-[0.16em] text-[#E5B85C] sm:text-xs sm:tracking-[0.2em]">Best fare found</p>
                      <h2 className="mt-1 text-xl font-black sm:mt-2 sm:text-3xl">{result.origin} <ArrowRight className="inline h-5 w-5 text-[#E5B85C] sm:h-6 sm:w-6" /> {result.destination}</h2>
                      {cheapest?.destinationName && (
                        <p className="mt-1 text-sm font-black text-[#FFFAF1]/90">{cheapest.destinationName}{cheapest.destinationCountry ? `, ${cheapest.destinationCountry}` : ""}</p>
                      )}
                      <p className="mt-2 text-xs leading-5 text-[#FFFAF1]/72 sm:text-sm">{resultSummary}</p>
                    </div>
                    <div className="rounded-xl bg-[#FFFAF1] px-3 py-2 text-[#17211D] sm:min-w-[9rem] sm:px-4 sm:py-3">
                      <p className="text-[11px] font-black uppercase text-[#516259]">Cheapest</p>
                      <p className="mt-0.5 text-xl font-black sm:text-2xl">{money(cheapest.price, result.currency)}</p>
                    </div>
                  </div>

                  <div className="mt-3 grid gap-2 rounded-xl border border-white/10 bg-white/10 p-3 sm:grid-cols-[1fr_auto] sm:items-end">
                    <div className="grid grid-cols-3 gap-2 text-[11px] font-bold text-[#FFFAF1]/86 sm:text-xs">
                      <p className="min-w-0">
                        <span className="flex items-center gap-1 text-[#E5B85C]"><Plane className="h-3.5 w-3.5" /> Depart</span>
                        <span className="mt-1 block truncate text-[#FFFAF1]">{readableDate(cheapest.departureDate)}</span>
                      </p>
                      <p className="min-w-0">
                        <span className="flex items-center gap-1 text-[#E5B85C]"><CalendarDays className="h-3.5 w-3.5" /> Return</span>
                        <span className="mt-1 block truncate text-[#FFFAF1]">{readableDate(cheapest.returnDate)}</span>
                      </p>
                      <p className="min-w-0">
                        <span className="flex items-center gap-1 text-[#E5B85C]"><Clock3 className="h-3.5 w-3.5" /> Duration</span>
                        <span className="mt-1 block truncate text-[#FFFAF1]">{cheapest.durationLabel || `${cheapest.tripDays} days`}</span>
                      </p>
                    </div>
                    <a
                      href={cheapest.liveFareUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-[#E5B85C] px-4 text-sm font-black text-[#17211D] hover:bg-[#f0c96f] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FFFAF1]"
                    >
                      Book now <ArrowRight className="h-4 w-4" />
                    </a>
                  </div>

                  <div className="mt-2 flex flex-wrap gap-1.5 text-[11px] font-black text-[#FFFAF1]/85 sm:gap-2 sm:text-xs">
                    <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 sm:px-3"><Plane className="h-3.5 w-3.5" /> {result.stops === "one_stop_or_fewer" ? "1 or less stops" : result.stops}</span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 sm:px-3"><Briefcase className="h-3.5 w-3.5" /> {travelClassLabel(result.travelClass)}</span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 sm:px-3"><Briefcase className="h-3.5 w-3.5" /> {result.carryOnBags} cabin</span>
                    <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 sm:px-3"><Briefcase className="h-3.5 w-3.5" /> {result.checkedBags} check-in</span>
                    {result.maxLayoverMinutes && (
                      <span className="inline-flex items-center gap-1 rounded-full bg-white/10 px-2.5 py-1 sm:px-3"><Clock3 className="h-3.5 w-3.5" /> {layoverLabel(result.maxLayoverMinutes)}</span>
                    )}
                  </div>
                </div>

                {exactDeal && (
                  <section>
                    <h2 className={`mb-3 text-sm font-black uppercase tracking-[0.14em] ${sectionTitleClass}`}>Your exact dates</h2>
                    <div className="grid grid-cols-3 gap-2 sm:gap-3">
                      <DealCard item={exactDeal} currency={result.currency} highlightCheapest={resultKey(exactDeal) === resultKey(cheapest)} />
                    </div>
                  </section>
                )}

                {requestedResultsToShow.length > 0 && (
                  <section>
                    <h2 className={`mb-3 text-sm font-black uppercase tracking-[0.14em] ${sectionTitleClass}`}>
                      {result.engine === "google_travel_explore" || result.engine === "google_flights_deals" ? `Cheapest anywhere deals ? ${{ weekend: "Weekend", one_week: "One week", two_weeks: "Two weeks" }[result.requestedRange?.tripDuration] || anywhereDurationLabel}` : `Best deals for ${form.trip_days} days`}
                    </h2>
                    <DealRail>
                      {requestedResultsToShow.map((item) => (
                        <div key={resultKey(item)} className="w-[43%] min-w-[9.5rem] shrink-0 snap-start sm:w-[12rem]">
                          <DealCard item={item} currency={result.currency} highlightCheapest={resultKey(item) === resultKey(cheapest)} baselinePrice={requestedBestPrice} />
                        </div>
                      ))}
                    </DealRail>
                  </section>
                )}

                {nearbyDurationResults.length > 0 && (
                  <section>
                    <h2 className={`mb-3 text-sm font-black uppercase tracking-[0.14em] ${sectionTitleClass}`}>Best nearby durations</h2>
                    <DealRail>
                      {nearbyDurationResults.map((item) => (
                        <div key={resultKey(item)} className="w-[43%] min-w-[9.5rem] shrink-0 snap-start sm:w-[12rem]">
                          <DealCard item={item} currency={result.currency} highlightCheapest={Number(item.price) === nearbyBestPrice} baselinePrice={nearbyBestPrice} />
                        </div>
                      ))}
                    </DealRail>
                  </section>
                )}

                {otherResults.length > 0 && (
                  <section className="tp-paper rounded-[1.15rem] p-3 sm:rounded-[1.5rem] sm:p-5">
                    <div className="mb-3 flex flex-wrap items-start justify-between gap-2">
                      <div>
                        <h2 className="text-sm font-black uppercase tracking-[0.14em] text-[#516259]">More fare options</h2>
                        <p className="mt-1 text-xs font-semibold text-[#516259]">
                          {visibleOtherResults.length} shown from {filteredOtherResults.length}
                        </p>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {[
                          ...(isAnywhereResult
                            ? [
                              ["all", "All"],
                              ["lowest", "Lowest fares"],
                              ["nonstop", "Nonstop"],
                            ]
                            : [
                              ["all", "All"],
                              ["requested", `${form.trip_days} days`],
                              ["shorter", "Shorter"],
                              ["longer", "Longer"],
                            ]),
                        ].map(([value, label]) => (
                          <button
                            key={value}
                            type="button"
                            onClick={() => setRestFilter(value)}
                            className={`min-h-8 rounded-full px-3 text-[11px] font-black transition ${restFilter === value ? "bg-[#17211D] text-[#FFFAF1]" : "bg-[#E7F2ED] text-[#2D7A65] hover:bg-[#d9ebe4]"}`}
                          >
                            {label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {filteredOtherResults.length === 0 ? (
                      <div className="rounded-xl border border-[#17211D]/10 bg-[#FFFCF7] p-4 text-center text-sm font-bold text-[#516259]">
                        No deals match this filter.
                      </div>
                    ) : (
                      <>
                        <div className="grid gap-2 sm:grid-cols-2 xl:grid-cols-3">
                        {visibleOtherResults.map((item) => (
                          <article
                            key={resultKey(item)}
                            className="relative overflow-hidden rounded-xl border border-[#17211D]/10 bg-[#FFFCF7] p-2.5 shadow-[0_10px_22px_rgba(23,33,29,0.06)] transition hover:-translate-y-0.5 hover:border-[#2D7A65]/25 hover:shadow-[0_14px_26px_rgba(23,33,29,0.09)]"
                          >
                            <span className="absolute inset-y-2.5 left-0 w-1 rounded-r-full bg-[#2D7A65]/45" />
                            <div className="flex items-start justify-between gap-2 pl-1.5">
                              <div className="min-w-0">
                                <div className="flex flex-wrap items-center gap-1.5">
                                  <p className="inline-flex items-center gap-1 rounded-full bg-[#E7F2ED] px-2 py-0.5 text-[11px] font-black text-[#2D7A65]">
                                    <Clock3 className="h-3 w-3" /> {item.durationLabel}
                                  </p>
                                  <p className="inline-flex items-center rounded-full bg-[#F7F1E7] px-2 py-0.5 text-[11px] font-black text-[#516259]">
                                    {fareDeltaLabel(item.price, filteredOtherBestPrice, result.currency)}
                                  </p>
                                </div>
                                {item.destinationName && (
                                  <p className="mt-1 truncate text-xs font-bold text-[#516259]">{item.destinationName}</p>
                                )}
                              </div>
                              <div className="shrink-0 text-right">
                                <p className="text-base font-black text-[#17211D]">{money(item.price, result.currency)}</p>
                              </div>
                            </div>
                            <div className="mt-2 grid grid-cols-[1fr_auto] items-center gap-2">
                              <div className="rounded-lg border border-[#17211D]/10 bg-white/55 px-2 py-1.5">
                                <div className="grid grid-cols-[1fr_auto_1fr] items-center gap-2 text-[11px] font-bold text-[#516259]">
                                  <p className="min-w-0">
                                    <span className="flex items-center gap-1 text-[#2D7A65]"><Plane className="h-3.5 w-3.5" /> Depart</span>
                                    <span className="mt-0.5 block truncate text-[#17211D]">{compactDate(item.departureDate)}</span>
                                  </p>
                                  <ArrowRight className="h-3.5 w-3.5 text-[#C5983C]" />
                                  <p className="min-w-0 text-right">
                                    <span className="flex items-center justify-end gap-1 text-[#2D7A65]"><CalendarDays className="h-3.5 w-3.5" /> Return</span>
                                    <span className="mt-0.5 block truncate text-[#17211D]">{compactDate(item.returnDate)}</span>
                                  </p>
                                </div>
                              </div>
                              <a href={item.liveFareUrl} target="_blank" rel="noreferrer" className="inline-flex min-h-8 items-center gap-1.5 rounded-full bg-[#E7F2ED] px-3 text-[11px] font-black text-[#2D7A65] hover:bg-[#d9ebe4] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#2D7A65] focus-visible:ring-offset-2">
                                <span className="whitespace-nowrap">Book now</span>
                                <ArrowRight className="h-3.5 w-3.5 shrink-0" />
                              </a>
                            </div>
                          </article>
                        ))}
                        </div>
                        {hasMoreOtherResults && (
                          <div ref={otherResultsLoadRef} className="mt-3 flex min-h-10 items-center justify-center rounded-xl bg-[#F7F1E7]/70 text-[11px] font-black text-[#516259]">
                            <Loader2 className="mr-2 h-3.5 w-3.5 animate-spin text-[#2D7A65]" />
                            Loading more fares
                          </div>
                        )}
                      </>
                    )}
                  </section>
                )}
              </div>
            )}
          </section>
        </section>
      </div>
    </Shell>
  );
}
